import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { components } from "../../apis/gen";
import {
	ApiError,
	append_session_with_claim,
	get_event_with_claim,
} from "../../apis/helper";
import { parseJwt } from "../../utils";
import { saveReturnUrl } from "../../utils/auth";
import {
	calculatePossibleStart,
	calculateUnifiedExpiry,
} from "../../utils/date-utils";
import { determineEventState, EVENT_STATES } from "../../utils/event-state";
import { isValidEmail } from "../../utils/validate-email";
import { GuestAppendView } from "./GuestAppendView";
import { GuestAppendState } from "./types";

const GAME_DURATION_SECONDS = 720; // 12 minutes
const MIN_GAME_DURATION_SECONDS = 60; // 1 minute

const GuestAppendPage: React.FC = () => {
	const { id: urlEventId } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");
	const { t } = useTranslation();
	const navigate = useNavigate();

	const jwtPayload = useMemo(() => (token ? parseJwt(token) : null), [token]);
	const eventId = urlEventId || jwtPayload?.sub;
	const _action = jwtPayload?.action ?? 0; // 0 for Append, 1 for CheckIn

	const [state, setState] = useState<GuestAppendState>(
		GuestAppendState.LANDING,
	);
	const [event, setEvent] = useState<components["schemas"]["Event"] | null>(
		null,
	);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [email, setEmail] = useState<string>("");
	const [emailError, setEmailError] = useState<string | null>(null);
	const [submitted, setSubmitted] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [showLoginModal, setShowLoginModal] = useState<boolean>(false);

	// Team setup state
	const [jerseyIsLight, setJerseyIsLight] = useState<boolean | null>(null);
	const [teamNameLight, setTeamNameLight] = useState<string>("");
	const [teamNameDark, setTeamNameDark] = useState<string>("");

	// Auto-transition: WAITING (welcome) → TEAM_SETUP after 2 seconds
	useEffect(() => {
		if (state !== GuestAppendState.WAITING || submitted) return;
		const timer = setTimeout(() => {
			setState(GuestAppendState.TEAM_SETUP);
		}, 2000);
		return () => clearTimeout(timer);
	}, [state, submitted]);

	// Token Expiration Moment from JWT
	const tokenExpiryMoment = useMemo(() => {
		if (!token) return null;
		const payload = parseJwt(token);
		return payload?.exp ? new Date(payload.exp * 1000) : null;
	}, [token]);

	const [tokenSecondsLeft, setTokenSecondsLeft] = useState(0);
	const [gameStartSecondsLeft, setGameStartSecondsLeft] = useState(0);

	// Heartbeat for countdowns
	useEffect(() => {
		const updateCountdowns = () => {
			const now = new Date();
			const nowMs = now.getTime();

			// 1. Unified Token/Link Countdown
			if (tokenExpiryMoment && event) {
				const eventEnd = event.ended_at ? new Date(event.ended_at) : null;
				if (eventEnd) {
					const unifiedExpiry = calculateUnifiedExpiry(
						tokenExpiryMoment,
						eventEnd,
					);
					const left = Math.max(
						0,
						Math.floor((unifiedExpiry.getTime() - nowMs) / 1000),
					);
					setTokenSecondsLeft(left);

					if (left === 0 && !submitted && state !== GuestAppendState.EXPIRED) {
						setState(GuestAppendState.EXPIRED);
						setError(t("guest.errors.link_expired"));
					}
				}
			}

			// 2. Game Start Countdown (to match kiosk)
			if (event) {
				const stateResult = determineEventState(event, nowMs);

				let displayCountdown = 0;
				if (stateResult.hasNextSession) {
					// Time to the already scheduled next game
					displayCountdown = stateResult.countdown;
				} else {
					// No next game scheduled yet, calculate time until the next possible start
					let latestSessionEnd: Date | null = null;
					if (event.sessions && event.sessions.length > 0) {
						const ends = event.sessions.map((s) =>
							new Date(s.ended_at).getTime(),
						);
						latestSessionEnd = new Date(Math.max(...ends));
					}

					const possibleStart = calculatePossibleStart(
						now,
						latestSessionEnd,
						new Date(event.started_at),
					);
					displayCountdown = Math.max(
						0,
						Math.floor((possibleStart.getTime() - nowMs) / 1000),
					);
				}
				setGameStartSecondsLeft(displayCountdown);

				// Drive WAITING → COUNTDOWN → GAME_START transitions after submission
				if (submitted) {
					if (stateResult.isPlayingState) {
						setState(GuestAppendState.GAME_START);
					} else if (stateResult.state === EVENT_STATES.WAITING_IMMINENT) {
						setState(GuestAppendState.COUNTDOWN);
					}
				}
			}
		};

		updateCountdowns();
		const interval = setInterval(updateCountdowns, 1000);
		return () => clearInterval(interval);
	}, [tokenExpiryMoment, event, t, submitted, state]);

	// Polling for event updates after submission to catch the new session
	useEffect(() => {
		if (!submitted || !eventId || !token) return;

		const poll = async () => {
			try {
				const data = await get_event_with_claim(eventId, token);
				setEvent(data);
			} catch (_err) {
				// Ignore polling errors for now
			}
		};

		const interval = setInterval(poll, 5000);
		return () => clearInterval(interval);
	}, [submitted, eventId, token]);

	useEffect(() => {
		if (!eventId || !token) {
			setError(t("guest.errors.invalid_link"));
			setLoading(false);
			return;
		}

		const fetchEvent = async () => {
			try {
				const data = await get_event_with_claim(eventId, token);
				setEvent(data);

				// Initial validation: can we even start a 1-minute game?
				const now = new Date();
				const eventEnd = data.ended_at ? new Date(data.ended_at) : null;

				let latestSessionEnd: Date | null = null;
				if (data.sessions && data.sessions.length > 0) {
					const ends = data.sessions.map((s) => new Date(s.ended_at).getTime());
					latestSessionEnd = new Date(Math.max(...ends));
				}

				const possibleStart = calculatePossibleStart(
					now,
					latestSessionEnd,
					new Date(data.started_at),
				);

				if (
					!eventEnd ||
					eventEnd.getTime() - possibleStart.getTime() <
						MIN_GAME_DURATION_SECONDS * 1000
				) {
					setError(t("guest.errors.link_expired"));
					setState(GuestAppendState.EXPIRED);
				} else {
					setState(GuestAppendState.LANDING);
				}
			} catch (err) {
				if (
					err instanceof ApiError &&
					(err.status === 401 || err.status === 403)
				) {
					setShowLoginModal(true);
				} else {
					setError(t("guest.errors.link_expired"));
					setState(GuestAppendState.EXPIRED);
				}
			} finally {
				setLoading(false);
			}
		};

		fetchEvent();
	}, [eventId, token, t]);

	// Polling for event updates
	useEffect(() => {
		if (!eventId || !token) return;

		const poll = async () => {
			try {
				const data = await get_event_with_claim(eventId, token);
				setEvent(data);
			} catch (_err) {
				// Ignore polling errors
			}
		};

		// Poll every 10 seconds to keep countdowns accurate if others book or sessions update
		const interval = setInterval(poll, 10000);
		return () => clearInterval(interval);
	}, [eventId, token]);

	const handleSetEmail = (value: string) => {
		setEmail(value);
		if (emailError) setEmailError(null);
	};

	const handleEmailBlur = () => {
		if (email && !isValidEmail(email)) {
			setEmailError(t("common.errors.invalid_email"));
		}
	};

	const handleEmailConfirm = () => {
		if (!email) return;
		if (!isValidEmail(email)) {
			setEmailError(t("common.errors.invalid_email"));
			return;
		}
		setState(GuestAppendState.WAITING);
	};

	const handleSubmitGame = async () => {
		if (!email || !eventId || !token || !event) return;

		setIsSubmitting(true);
		try {
			const now = new Date();

			// Find the latest end time of existing sessions
			let latestSessionEnd: Date | null = null;
			if (event.sessions && event.sessions.length > 0) {
				const ends = event.sessions.map((s) => new Date(s.ended_at).getTime());
				latestSessionEnd = new Date(Math.max(...ends));
			}

			const startedAt = calculatePossibleStart(
				now,
				latestSessionEnd,
				new Date(event.started_at),
			);
			const eventEnd = event.ended_at ? new Date(event.ended_at) : startedAt;

			// Target 12 minutes, but truncate to eventEnd
			const targetEnd = new Date(
				startedAt.getTime() + GAME_DURATION_SECONDS * 1000,
			);
			const endedAt = targetEnd > eventEnd ? eventEnd : targetEnd;

			// Final sanity check
			if (
				endedAt.getTime() - startedAt.getTime() <
				MIN_GAME_DURATION_SECONDS * 1000
			) {
				setError(t("guest.errors.insufficient_time"));
				setState(GuestAppendState.EXPIRED);
				return;
			}

			const lightName = teamNameLight || t("guest.team_setup.default_light");
			const darkName = teamNameDark || t("guest.team_setup.default_dark");
			const payload = {
				name: t("guest.game_name"),
				desc: t("guest.appended_by"),
				started_at: startedAt.toISOString(),
				ended_at: endedAt.toISOString(),
				appender_email: email,
				jersey_is_light: jerseyIsLight ?? true,
				team_names: [lightName, darkName],
			} as components["schemas"]["AppendSessionPayload"];

			await append_session_with_claim(eventId, token, payload);
			setSubmitted(true);
			setState(GuestAppendState.COUNTDOWN);
		} catch (err) {
			if (
				err instanceof ApiError &&
				(err.status === 401 || err.status === 403)
			) {
				setShowLoginModal(true);
			} else if (err instanceof ApiError && err.status === 409) {
				setError(t("guest.errors.camera_busy"));
			} else {
				setError(t("guest.errors.book_failed"));
				setState(GuestAppendState.EXPIRED);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const formattedTokenTime = useMemo(() => {
		const mins = Math.floor(tokenSecondsLeft / 60);
		const secs = tokenSecondsLeft % 60;
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	}, [tokenSecondsLeft]);

	const formattedGameStartTime = useMemo(() => {
		const mins = Math.floor(gameStartSecondsLeft / 60);
		const secs = gameStartSecondsLeft % 60;
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	}, [gameStartSecondsLeft]);

	return (
		<GuestAppendView
			state={state}
			event={event}
			loading={loading}
			error={error}
			email={email}
			setEmail={handleSetEmail}
			emailError={emailError}
			onEmailBlur={handleEmailBlur}
			onConfirm={handleEmailConfirm}
			isSubmitting={isSubmitting}
			submitted={submitted}
			timeLeft={formattedTokenTime}
			gameStartLeft={formattedGameStartTime}
			showLoginModal={showLoginModal}
			onCloseLoginModal={() => setShowLoginModal(false)}
			onLogin={() => {
				setShowLoginModal(false);
				saveReturnUrl();
				navigate("/login");
			}}
			jerseyIsLight={jerseyIsLight}
			setJerseyIsLight={setJerseyIsLight}
			teamNameLight={teamNameLight}
			setTeamNameLight={setTeamNameLight}
			teamNameDark={teamNameDark}
			setTeamNameDark={setTeamNameDark}
			onConfirmTeamSetup={handleSubmitGame}
		/>
	);
};

export { GuestAppendPage };
